import React, { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { api, fileUrl } from "@/lib/api";
import { Image as ImageIcon } from "lucide-react";

const FALLBACK_PHOTOS = [
  {
    image_url:
      "https://images.unsplash.com/photo-1592805144716-feeccccef5ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxkYXJrJTIwc2VtaSUyMHRydWNrJTIwaGlnaHdheXxlbnwwfHx8fDE3ODA5MzYxMDF8MA&ixlib=rb-4.1.0&q=85",
    label: "FLEET / 01",
    span: "md:col-span-2 md:row-span-2",
  },
  {
    image_url:
      "https://images.pexels.com/photos/10410106/pexels-photo-10410106.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    label: "WAREHOUSE",
  },
  {
    image_url:
      "https://images.pexels.com/photos/30341205/pexels-photo-30341205.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    label: "LOGISTICS",
  },
  {
    image_url:
      "https://images.unsplash.com/photo-1485575301924-6891ef935dcd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxkYXJrJTIwc2VtaSUyMHRydWNrJTIwaGlnaHdheXxlbnwwfHx8fDE3ODA5MzYxMDF8MA&ixlib=rb-4.1.0&q=85",
    label: "ON THE ROAD",
    span: "md:col-span-2",
  },
  {
    image_url:
      "https://images.unsplash.com/photo-1626712211690-8de4fe30177c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHx0cnVjayUyMGRyaXZlciUyMHBvcnRyYWl0fGVufDB8fHx8MTc4MDkzNjEwMXww&ixlib=rb-4.1.0&q=85",
    label: "DRIVER",
  },
  {
    image_url:
      "https://images.pexels.com/photos/9816965/pexels-photo-9816965.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    label: "PORTRAIT",
  },
];

const spanFor = (i) => {
  if (i === 0) return "md:col-span-2 md:row-span-2";
  if (i === 3) return "md:col-span-2";
  return "";
};

export default function Gallery() {
  const { t } = useLanguage();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usingFallback, setUsingFallback] = useState(false);
  const [initialized, setInitialized] = useState(false);

  if (!initialized) {
    setInitialized(true);
    api
      .get("/gallery")
      .then((res) => {
        const data = res.data || [];
        if (data.length === 0) {
          setPhotos(FALLBACK_PHOTOS);
          setUsingFallback(true);
        } else {
          setPhotos(data);
          setUsingFallback(false);
        }
      })
      .catch(() => {
        setPhotos(FALLBACK_PHOTOS);
        setUsingFallback(true);
      })
      .finally(() => setLoading(false));
  }

  return (
    <div data-testid="gallery-page" className="pt-28 md:pt-36">
      <section className="px-6 md:px-12 lg:px-16">
        <div className="text-xs tracking-[0.3em] uppercase text-[#ff5a00] font-mono mb-4">
          / 03 — {t.gallery.title}
        </div>
        <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl uppercase leading-none">
          {t.gallery.subtitle}
        </h1>
        <p className="mt-8 max-w-2xl text-zinc-400 text-base md:text-lg">
          {t.gallery.lead}
        </p>
      </section>

      <section className="px-6 md:px-12 lg:px-16 mt-16 md:mt-20">
        {loading ? (
          <div className="text-zinc-500 font-mono text-sm">
            {t.common.loading}
          </div>
        ) : (
          <>
            {usingFallback && (
              <div className="mb-6 inline-flex items-center gap-2 text-[10px] font-mono tracking-widest uppercase text-zinc-500 border border-zinc-800 px-3 py-1">
                <ImageIcon size={12} />
                Demo · Ladda upp egna bilder från admin
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 md:gap-4 auto-rows-[220px] md:auto-rows-[260px]">
              {photos.map((p, i) => {
                const src = p.image_url?.startsWith("http")
                  ? p.image_url
                  : fileUrl(p.image_url);
                const span = p.span || spanFor(i);
                return (
                  <figure
                    key={p.id || i}
                    data-testid={`gallery-tile-${i}`}
                    className={`relative overflow-hidden border border-zinc-800 group ${span}`}
                  >
                    <img
                      src={src}
                      alt={p.label || "Bränninger"}
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors" />
                    {p.label && (
                      <figcaption className="absolute bottom-3 left-3 text-[10px] font-mono tracking-[0.3em] text-white bg-black/60 px-2 py-1 backdrop-blur-sm">
                        {p.label}
                      </figcaption>
                    )}
                  </figure>
                );
              })}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
